<template>
    <CModal
        title="Создать нулевой заказ"
        :show.sync="open"
        color="primary"
        size="lg"
    >
        <template>
            <div slot="body-wrapper" class="modal-body">
                <CForm>
                    <CRow class="mb-3">
                        <CCol>
                            <div class="col-form-label">Телефон клиента *</div>
                            <CInput
                                v-model="customerPhone"
                                placeholder="+79991234567"
                                @input="customerFound = null"
                            />
                            <div v-if="customerFound !== null" class="mt-1">
                                <span v-if="customerFound" class="text-success">
                                    ✓ Клиент найден: {{ customerFound.firstname || customerPhone }}
                                </span>
                                <span v-else class="text-warning">
                                    ⚠ Клиент не найден. Будет создан новый.
                                </span>
                            </div>
                        </CCol>
                    </CRow>
                    <CRow class="mb-3">
                        <CCol>
                            <div class="col-form-label">Тип сервиса *</div>
                            <CSelect
                                v-model="selectedServiceType"
                                :options="serviceTypeOptions"
                                placeholder="Выберите тип сервиса"
                                @change="onServiceTypeChange"
                            />
                        </CCol>
                    </CRow>
                    <CRow class="mb-3">
                        <CCol>
                            <div class="col-form-label">Сервис *</div>
                            <CSelect
                                v-model="selectedServiceId"
                                :options="serviceOptions"
                                :disabled="!selectedServiceType || loadingServices"
                                placeholder="Выберите сервис"
                            />
                            <div v-if="loadingServices" class="text-muted mt-1">
                                Загрузка списка сервисов...
                            </div>
                            <div v-if="!selectedServiceType" class="text-muted mt-1">
                                Сначала выберите тип сервиса
                            </div>
                        </CCol>
                    </CRow>
                </CForm>
            </div>
            <div slot="footer">
                <CButton
                    color="light"
                    @click="closeModal"
                >
                    Отмена
                </CButton>
                <CButton
                    color="primary"
                    :disabled="!customerPhone || !selectedServiceType || !selectedServiceId || creating"
                    @click="createZeroOrder"
                >
                    {{ creating ? 'Создание...' : 'Создать заказ' }}
                </CButton>
            </div>
        </template>
    </CModal>
</template>

<script>
    import Vue from 'vue';

    export default {
        name: 'ZeroOrderModal',

        props: {
            open: {
                type: Boolean,
                default: false
            }
        },

        data() {
            return {
                customerPhone: '',
                customerFound: null,
                selectedServiceType: null,
                selectedServiceId: null,
                loadingServices: false,
                creating: false,
                services: [],
                serviceTypeOptions: [
                    { value: 'restaurant', label: 'Ресторан' },
                    { value: 'grocer', label: 'Магазин' },
                    { value: 'entity', label: 'Другое' }
                ]
            };
        },

        computed: {
            serviceOptions() {
                if (!this.services.length) {
                    return [];
                }
                return this.services.map(service => ({
                    value: service.id,
                    label: service.title || service.name
                }));
            }
        },

        methods: {
            async onServiceTypeChange() {
                this.selectedServiceId = null;
                this.services = [];
                
                if (!this.selectedServiceType) {
                    return;
                }

                await this.loadServices();
            },

            async loadServices() {
                this.loadingServices = true;
                
                try {
                    let endpoint = '';
                    switch (this.selectedServiceType) {
                        case 'restaurant':
                            endpoint = '/restaurants';
                            break;
                        case 'grocer':
                            endpoint = '/grocers';
                            break;
                        case 'entity':
                            endpoint = '/entities-type';
                            break;
                    }

                    const response = await Vue.axios.get(endpoint);
                    
                    // Для entities нужно получить все сервисы всех типов
                    if (this.selectedServiceType === 'entity') {
                        const entityTypes = response.data;
                        let allEntities = [];
                        
                        for (const entityType of entityTypes) {
                            const entitiesResponse = await Vue.axios.get(`/entities/${entityType.code}`);
                            if (entitiesResponse.data && Array.isArray(entitiesResponse.data)) {
                                allEntities = allEntities.concat(entitiesResponse.data);
                            }
                        }
                        
                        this.services = allEntities.filter(e => e.enabled);
                    } else {
                        this.services = response.data.filter(s => s.enabled);
                    }
                } catch (err) {
                    this.$root.$emit('showError', 'Ошибка загрузки списка сервисов. ' + err);
                } finally {
                    this.loadingServices = false;
                }
            },

            async createZeroOrder() {
                if (!this.customerPhone || !this.selectedServiceType || !this.selectedServiceId) {
                    return;
                }

                this.creating = true;

                try {
                    // Сначала проверяем существование клиента
                    await this.checkCustomer();
                    
                    const response = await Vue.axios.post('/orders/create-zero', {
                        customer_phone: this.customerPhone,
                        service_type: this.selectedServiceType,
                        service_id: this.selectedServiceId
                    });

                    if (response.data && response.data.id) {
                        this.$root.$emit('showSuccess', 'Нулевой заказ успешно создан');
                        this.$emit('order-created', response.data);
                        this.closeModal();
                    } else {
                        this.$root.$emit('showError', 'Ошибка создания заказа');
                    }
                } catch (err) {
                    this.$root.$emit('showError', 'Ошибка создания нулевого заказа. ' + err);
                } finally {
                    this.creating = false;
                }
            },

            async checkCustomer() {
                if (!this.customerPhone) {
                    return;
                }

                try {
                    const response = await Vue.axios.get('/customers', {
                        params: { 
                            search: this.customerPhone,
                            sortField: 'id',
                            sortOrder: 'desc'
                        }
                    });

                    if (response.data && response.data.results && response.data.results.length > 0) {
                        this.customerFound = response.data.results[0];
                    } else {
                        this.customerFound = false;
                    }
                } catch (err) {
                    console.error('Ошибка проверки клиента:', err);
                    this.customerFound = null;
                }
            },

            closeModal() {
                this.customerPhone = '';
                this.customerFound = null;
                this.selectedServiceType = null;
                this.selectedServiceId = null;
                this.services = [];
                this.$emit('close');
            }
        },

        watch: {
            customerPhone(val) {
                if (val && val.length > 10) {
                    this.checkCustomer();
                }
            }
        }
    };
</script>

<style scoped>
.modal-body {
    min-height: 200px;
}
</style>

